import React, { useState, useEffect, useContext } from 'react';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Star,
    Check as ApproveIcon,
    Close as RejectIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Save as SaveIcon,
    X
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import reviewsService from '../../services/reviewsService';

const AdminReviewsManager = () => {
    const { currentUser } = useContext(AuthContext);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [selectedReview, setSelectedReview] = useState(null);
    const [viewDialog, setViewDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [reviewToDelete, setReviewToDelete] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        rating: 'all',
        featured: 'all',
        search: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);

    useEffect(() => {
        if (currentUser?.is_admin) {
            loadReviews();
        }
    }, [filters, currentPage, currentUser]);

    const loadReviews = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await reviewsService.getReviews({
                skip: (currentPage - 1) * 10,
                limit: 10,
                featuredOnly: filters.featured === 'featured',
                minRating: filters.rating === 'all' ? 1 : parseInt(filters.rating)
            });

            // Apply client-side filtering
            let filteredData = data;

            if (filters.search) {
                filteredData = data.filter(review =>
                    review.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                    review.company.toLowerCase().includes(filters.search.toLowerCase()) ||
                    review.comment.toLowerCase().includes(filters.search.toLowerCase())
                );
            }

            if (filters.status !== 'all') {
                filteredData = filteredData.filter(review =>
                    filters.status === 'approved' ? review.is_approved : !review.is_approved
                );
            }

            setReviews(filteredData);
            setTotalReviews(filteredData.length);
        } catch (err) {
            setError('Failed to load reviews: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const openEditDialog = (review) => {
        // Convert date to datetime-local format if needed
        const formattedDate = review.created_at
            ? new Date(review.created_at).toISOString().slice(0, 16)
            : new Date().toISOString().slice(0, 16);

        setEditingReview({
            ...review,
            created_at: formattedDate
        });
        setEditDialog(true);
    };

    const handleEditReview = async () => {
        if (!editingReview) return;

        try {
            const token = localStorage.getItem('accessToken');
            await reviewsService.updateReview(editingReview.id, editingReview, token);
            setSuccess('Review updated successfully');
            loadReviews();
            setEditDialog(false);
            setEditingReview(null);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update review: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleApproveToggle = async (reviewId, currentStatus) => {
        try {
            const token = localStorage.getItem('accessToken');
            await reviewsService.approveReview(reviewId, token);
            setSuccess(`Review ${currentStatus ? 'disapproved' : 'approved'} successfully`);
            loadReviews();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update review approval: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleFeatureToggle = async (reviewId, currentStatus) => {
        try {
            const token = localStorage.getItem('accessToken');
            await reviewsService.featureReview(reviewId, token);
            setSuccess(`Review ${currentStatus ? 'unfeatured' : 'featured'} successfully`);
            loadReviews();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to update review feature status: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleDeleteReview = async () => {
        if (!reviewToDelete) return;

        try {
            const token = localStorage.getItem('accessToken');
            await reviewsService.deleteReview(reviewToDelete.id, token);
            setSuccess('Review deleted successfully');
            setReviews(reviews.filter(r => r.id !== reviewToDelete.id));
            setDeleteDialog(false);
            setReviewToDelete(null);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete review: ' + err.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const StarRating = ({ rating, readOnly = true, onRatingChange }) => {
        if (readOnly) {
            return (
                <div style={{ display: 'flex', gap: '2px' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                            key={star}
                            style={{
                                fontSize: '16px',
                                color: star <= rating ? '#fbbf24' : '#d1d5db'
                            }}
                        />
                    ))}
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', gap: '2px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        onClick={() => onRatingChange && onRatingChange(star)}
                        style={{
                            fontSize: '20px',
                            color: star <= rating ? '#fbbf24' : '#d1d5db',
                            cursor: 'pointer'
                        }}
                    />
                ))}
            </div>
        );
    };

    const getStatusChip = (review) => {
        if (!review.is_approved) {
            return (
                <span style={{
                    background: '#fbbf24',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>
                    Pending
                </span>
            );
        }
        if (review.is_featured) {
            return (
                <span style={{
                    background: '#2563eb',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>
                    Featured
                </span>
            );
        }
        return (
            <span style={{
                background: '#16a34a',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500'
            }}>
                Approved
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Check if user is admin - moved after hooks
    if (!currentUser?.is_admin) {
        return (
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px' }}>
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '16px',
                    color: '#dc2626'
                }}>
                    Access denied. Admin privileges required.
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f4f6',
                        borderTop: '4px solid #2563eb',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 16px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                    Reviews Management
                </h1>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        Total Reviews: {totalReviews}
                    </span>
                    <button
                        onClick={loadReviews}
                        disabled={loading}
                        style={{
                            background: 'white',
                            border: '2px solid #2563eb',
                            color: '#2563eb',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Alerts */}
            {error && (
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#dc2626',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>{error}</span>
                    <button
                        onClick={() => setError('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                    >
                        <X style={{ fontSize: '20px' }} />
                    </button>
                </div>
            )}
            {success && (
                <div style={{
                    background: '#dcfce7',
                    border: '1px solid #bbf7d0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#16a34a',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span>{success}</span>
                    <button
                        onClick={() => setSuccess('')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a' }}
                    >
                        <X style={{ fontSize: '20px' }} />
                    </button>
                </div>
            )}

            {/* Filters */}
            <div style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                marginBottom: '24px'
            }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <FilterIcon style={{ fontSize: '20px', marginRight: '8px' }} />
                    Filters
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', alignItems: 'end' }}>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Search
                        </label>
                        <div style={{ position: 'relative' }}>
                            <SearchIcon style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#6b7280' }} />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px 8px 36px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '14px'
                                }}
                                placeholder="Search reviews..."
                            />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Status
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="all">All</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Rating
                        </label>
                        <select
                            value={filters.rating}
                            onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="all">All</option>
                            <option value="5">5 Stars</option>
                            <option value="4">4 Stars</option>
                            <option value="3">3 Stars</option>
                            <option value="2">2 Stars</option>
                            <option value="1">1 Star</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                            Featured
                        </label>
                        <select
                            value={filters.featured}
                            onChange={(e) => setFilters({ ...filters, featured: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="all">All</option>
                            <option value="featured">Featured</option>
                            <option value="not_featured">Not Featured</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            onClick={loadReviews}
                            style={{
                                background: '#2563eb',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Apply
                        </button>
                        <button
                            onClick={() => setFilters({
                                status: 'all',
                                rating: 'all',
                                featured: 'all',
                                search: ''
                            })}
                            style={{
                                background: 'white',
                                color: '#6b7280',
                                border: '1px solid #d1d5db',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>

            {/* Reviews Table */}
            <div style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                marginBottom: '24px'
            }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f9fafb' }}>
                            <tr>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                                    Customer
                                </th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                                    Rating
                                </th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                                    Status
                                </th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                                    Date
                                </th>
                                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {reviews.map((review, index) => (
                                <tr key={review.id} style={{ borderBottom: index < reviews.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '32px',
                                                height: '32px',
                                                background: '#2563eb',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: '14px',
                                                fontWeight: 'bold'
                                            }}>
                                                {review.name.charAt(0)}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '600', color: '#111827' }}>
                                                    {review.name}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                    {review.company || 'No company'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <StarRating rating={review.rating} />
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        {getStatusChip(review)}
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                            {formatDate(review.created_at || review.date)}
                                        </div>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => { setSelectedReview(review); setViewDialog(true); }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: '#6b7280'
                                                }}
                                                title="View Details"
                                            >
                                                <ViewIcon style={{ fontSize: '16px' }} />
                                            </button>
                                            <button
                                                onClick={() => openEditDialog(review)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: '#7c3aed'
                                                }}
                                                title="Edit Review"
                                            >
                                                <EditIcon style={{ fontSize: '16px' }} />
                                            </button>
                                            <button
                                                onClick={() => handleApproveToggle(review.id, review.is_approved)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: review.is_approved ? '#16a34a' : '#6b7280'
                                                }}
                                                title={review.is_approved ? "Disapprove" : "Approve"}
                                            >
                                                {review.is_approved ? <ApproveIcon style={{ fontSize: '16px' }} /> : <RejectIcon style={{ fontSize: '16px' }} />}
                                            </button>
                                            <button
                                                onClick={() => handleFeatureToggle(review.id, review.is_featured)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: review.is_featured ? '#2563eb' : '#6b7280'
                                                }}
                                                title={review.is_featured ? "Unfeature" : "Feature"}
                                            >
                                                <Star style={{ fontSize: '16px' }} />
                                            </button>
                                            <button
                                                onClick={() => { setReviewToDelete(review); setDeleteDialog(true); }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    borderRadius: '4px',
                                                    color: '#ef4444'
                                                }}
                                                title="Delete"
                                            >
                                                <DeleteIcon style={{ fontSize: '16px' }} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {reviews.length === 0 && (
                    <div style={{ padding: '64px', textAlign: 'center' }}>
                        <div style={{ color: '#6b7280' }}>
                            No reviews found matching your criteria.
                        </div>
                    </div>
                )}
            </div>

            {/* View Review Dialog */}
            {viewDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50,
                    padding: '16px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '600px',
                        width: '100%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                                    Review Details
                                </h3>
                                <button
                                    onClick={() => setViewDialog(false)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                                >
                                    <X style={{ fontSize: '24px' }} />
                                </button>
                            </div>
                        </div>
                        <div style={{ padding: '24px' }}>
                            {selectedReview && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                                            Customer Name
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#111827', marginBottom: '16px' }}>
                                            {selectedReview.name}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                                            Company
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#111827', marginBottom: '16px' }}>
                                            {selectedReview.company || 'Not provided'}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                                            Rating
                                        </div>
                                        <div style={{ marginBottom: '16px' }}>
                                            <StarRating rating={selectedReview.rating} />
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                                            Date Submitted
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#111827', marginBottom: '16px' }}>
                                            {formatDate(selectedReview.created_at || selectedReview.date)}
                                        </div>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                                            Review Comment
                                        </div>
                                        <div style={{
                                            background: '#f9fafb',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            padding: '16px',
                                            lineHeight: '1.6',
                                            whiteSpace: 'pre-wrap',
                                            marginBottom: '24px'
                                        }}>
                                            {selectedReview.comment}
                                        </div>
                                    </div>
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <div style={{ display: 'flex', gap: '24px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedReview.is_featured || false}
                                                    onChange={() => handleFeatureToggle(selectedReview.id, selectedReview.is_featured)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <span>Featured</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
                            <button
                                onClick={() => setViewDialog(false)}
                                style={{
                                    background: '#6b7280',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Review Dialog */}
            {editDialog && editingReview && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50,
                    padding: '16px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                                    Edit Review
                                </h3>
                                <button
                                    onClick={() => setEditDialog(false)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                                >
                                    <X style={{ fontSize: '24px' }} />
                                </button>
                            </div>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Customer Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={editingReview.name}
                                        onChange={(e) => setEditingReview({ ...editingReview, name: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Company
                                    </label>
                                    <input
                                        type="text"
                                        value={editingReview.company || ''}
                                        onChange={(e) => setEditingReview({ ...editingReview, company: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Role
                                    </label>
                                    <input
                                        type="text"
                                        value={editingReview.role || ''}
                                        onChange={(e) => setEditingReview({ ...editingReview, role: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Date & Time
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={editingReview.created_at}
                                        onChange={(e) => setEditingReview({ ...editingReview, created_at: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                    Rating
                                </label>
                                <StarRating
                                    rating={editingReview.rating}
                                    onRatingChange={(rating) => setEditingReview({ ...editingReview, rating })}
                                    readOnly={false}
                                />
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                    Review Comment *
                                </label>
                                <textarea
                                    value={editingReview.comment}
                                    onChange={(e) => setEditingReview({ ...editingReview, comment: e.target.value })}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: '20px' }}>
                                <div style={{ display: 'flex', gap: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={editingReview.is_approved || false}
                                            onChange={(e) => setEditingReview({ ...editingReview, is_approved: e.target.checked })}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span>Approved</span>
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={editingReview.is_featured || false}
                                            onChange={(e) => setEditingReview({ ...editingReview, is_featured: e.target.checked })}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span>Featured</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setEditDialog(false)}
                                style={{
                                    background: 'white',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditReview}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <SaveIcon style={{ fontSize: '16px' }} />
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50,
                    padding: '16px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        maxWidth: '400px',
                        width: '100%'
                    }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
                                Confirm Delete
                            </h3>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <p style={{ color: '#374151', lineHeight: '1.6', margin: 0 }}>
                                Are you sure you want to delete this review from {reviewToDelete?.name}?
                                This action cannot be undone.
                            </p>
                        </div>
                        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setDeleteDialog(false)}
                                style={{
                                    background: 'white',
                                    color: '#6b7280',
                                    border: '1px solid #d1d5db',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteReview}
                                style={{
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AdminReviewsManager; 